// Readiness Quiz — 8 questions, three graded answers each.
//
// The earlier version asked eight yes/no statements adapted from the 360
// Assessment. These are Avi's own questions about where a business owner is
// actually stuck, and each one is answered on a three-step scale instead of a
// binary, so the middle ground ("partly", "roughly", "I have a guess") stops
// being forced into a yes or a no.

export type Language = 'he' | 'en';

export interface ReadinessOption {
  // Stable, language-independent. This is what gets stored on the lead, so it
  // must not change when the wording of a label does.
  key: string;
  label: { he: string; en: string };
  // 0 = no friction on this axis, 2 = the most friction. Ordering inside a
  // question follows the wording Avi wrote, which is not always low-to-high.
  score: 0 | 1 | 2;
}

export interface ReadinessQuestion {
  id: string;
  text: { he: string; en: string };
  options: ReadinessOption[];
}

export const readinessQuestions: ReadinessQuestion[] = [
  {
    id: 'rq1',
    text: {
      he: 'כמה החלטות בעסק עדיין מחכות רק לך כדי להתקדם?',
      en: "How many decisions in the business still can't move forward without you?",
    },
    options: [
      { key: 'very_few', label: { he: 'מעט מאוד', en: 'Very few' }, score: 0 },
      { key: 'some', label: { he: 'חלק', en: 'Some' }, score: 1 },
      { key: 'too_many', label: { he: 'יותר מדי', en: 'Too many' }, score: 2 },
    ],
  },
  {
    id: 'rq2',
    text: {
      he: 'אם אינך בעסק במשך חודש, הוא ימשיך לעבוד באותה רמה?',
      en: 'If you were away from the business for a month, would it keep running at the same level?',
    },
    options: [
      { key: 'yes', label: { he: 'כן', en: 'Yes' }, score: 0 },
      { key: 'partly', label: { he: 'חלקית', en: 'Partly' }, score: 1 },
      { key: 'really_not', label: { he: 'ממש לא', en: 'Definitely not' }, score: 2 },
    ],
  },
  {
    id: 'rq3',
    text: {
      he: 'האם יש היום בעסק בעיה משמעותית שאת/ה יודע/ת שקיימת, אבל עדיין לא הצלחת לפתור?',
      en: "Is there a significant problem in the business today that you know about but haven't managed to solve?",
    },
    options: [
      // Not low-to-high: here "yes" is the answer that carries the friction.
      { key: 'yes', label: { he: 'כן', en: 'Yes' }, score: 2 },
      { key: 'no', label: { he: 'לא', en: 'No' }, score: 0 },
      { key: 'not_sure', label: { he: 'לא בטוח', en: 'Not sure' }, score: 1 },
    ],
  },
  {
    id: 'rq4',
    text: {
      he: 'הנך יודע/ת בכל רגע נתון מה באמת מייצר לך רווח ומה רק מייצר מחזור?',
      en: 'Do you know at any given moment what actually makes you a profit and what only brings in revenue?',
    },
    options: [
      { key: 'yes', label: { he: 'כן', en: 'Yes' }, score: 0 },
      { key: 'roughly', label: { he: 'בערך', en: 'Roughly' }, score: 1 },
      { key: 'no', label: { he: 'לא', en: 'No' }, score: 2 },
    ],
  },
  {
    id: 'rq5',
    text: {
      he: 'כשיש כמה שותפים או בני משפחה בעסק, האם כולכם רואים את מצב החברה באותה צורה?',
      en: "If you have partners or family members in the business, do you all see the company's situation the same way?",
    },
    options: [
      { key: 'yes', label: { he: 'כן', en: 'Yes' }, score: 0 },
      { key: 'usually', label: { he: 'בדרך כלל', en: 'Usually' }, score: 1 },
      { key: 'really_not', label: { he: 'ממש לא', en: 'Not at all' }, score: 2 },
    ],
  },
  {
    id: 'rq6',
    text: {
      he: 'האם האנשים סביבך יודעים לקבל החלטות גם בלעדיך, או שבסוף הכול חוזר אליך?',
      en: 'Can the people around you make decisions without you, or does everything end up back with you?',
    },
    options: [
      { key: 'they_decide', label: { he: 'מקבלים החלטות', en: 'They make decisions' }, score: 0 },
      { key: 'depends', label: { he: 'תלוי בנושא', en: 'Depends on the issue' }, score: 1 },
      { key: 'back_to_me', label: { he: 'הכול חוזר אליי', en: 'Everything comes back to me' }, score: 2 },
    ],
  },
  {
    id: 'rq7',
    text: {
      he: 'האם העסק היום נמצא במקום שחשבת שיהיה בו לפני 3 שנים?',
      en: 'Three years ago, is this where you thought the business would be today?',
    },
    options: [
      { key: 'yes', label: { he: 'כן', en: 'Yes' }, score: 0 },
      { key: 'partly', label: { he: 'חלקית', en: 'Partly' }, score: 1 },
      { key: 'far_from_it', label: { he: 'רחוק מזה', en: 'Far from it' }, score: 2 },
    ],
  },
  {
    id: 'rq8',
    text: {
      he: 'אם הנך צריך לבחור דבר אחד שמעכב היום את הצמיחה של החברה, תדע/י להצביע עליו?',
      en: "If you had to pick the one thing holding back the company's growth today, could you point to it?",
    },
    options: [
      { key: 'definitely', label: { he: 'כן, בוודאות', en: 'Yes, definitely' }, score: 0 },
      { key: 'a_guess', label: { he: 'יש לי השערה', en: 'I have a hunch' }, score: 1 },
      { key: 'not_really', label: { he: 'לא באמת', en: 'Not really' }, score: 2 },
    ],
  },
];

// Every question contributes 0-2, so the friction scale runs 0-16. This is the
// finer of the two readings: it separates "חלקית" from "ממש לא", which the
// optimal-answer count below deliberately does not. Recorded on the lead for
// Avi, never shown to the person answering.
export const MAX_FRICTION = readinessQuestions.reduce(
  (total, q) => total + Math.max(...q.options.map((o) => o.score)),
  0,
);

export const getFrictionScore = (selected: Record<string, string>): number =>
  readinessQuestions.reduce((sum, q) => {
    const option = q.options.find((o) => o.key === selected[q.id]);
    return sum + (option ? option.score : 0);
  }, 0);

// The "right" answer per question, in the sense of a business that is actually
// realising its potential: the owner is not the bottleneck, the numbers are
// known, the problems are named. That is exactly the option carrying no
// friction, so the dictionary is derived from the scores rather than typed out
// twice and left to drift.
export const optimalAnswers: Record<string, string> = readinessQuestions.reduce(
  (map, q) => {
    const optimal = q.options.find((o) => o.score === 0);
    if (optimal) map[q.id] = optimal.key;
    return map;
  },
  {} as Record<string, string>,
);

export const countOptimalAnswers = (selected: Record<string, string>): number =>
  readinessQuestions.filter((q) => selected[q.id] === optimalAnswers[q.id]).length;

// Three segments over the count of optimal answers, 0-8. Nothing in the flow
// branches on these — everyone reaches the same form — but each segment gets a
// different reading of its own answers on the closing screen, so the person
// leaves with something even if they never pick up the phone.
export type ResultTier = 'emerging' | 'developing' | 'established';
const ESTABLISHED_THRESHOLD = 6; // 6-8 optimal answers
const DEVELOPING_THRESHOLD = 4; // 4-5 optimal answers
export const getResultTier = (optimalCount: number): ResultTier => {
  if (optimalCount >= ESTABLISHED_THRESHOLD) return 'established';
  if (optimalCount >= DEVELOPING_THRESHOLD) return 'developing';
  return 'emerging';
};

// What each segment is told on the closing screen. Deliberately a reading and
// nothing else: it names the pattern the answers describe and points at the one
// thing worth checking, which is also the natural opening for the call. No
// count is shown — the number is Avi's, not the reader's, and "0 מתוך 8" is a
// grade rather than an insight.
export interface TierInsight {
  title: string;
  body: string;
  focus: string;
}

export const tierInsights: Record<ResultTier, Record<Language, TierInsight>> = {
  emerging: {
    he: {
      title: 'כרגע כמעט הכול בעסק עובר דרכך',
      body: 'התשובות שלך מציירות עסק שרוב ההחלטות בו מחכות לך. ברוב המקרים זה לא סימן לניהול לא טוב, אלא לעסק שצמח מהר יותר מהמבנה שלו — והמבנה לא הספיק להדביק את הקצב.',
      focus: 'שווה לבדוק: מה הדבר האחד שאם ייפתר החודש ישחרר לך את רוב הזמן — וכמה הוא עולה לך היום, בכסף ובשעות.',
    },
    en: {
      title: 'Right now almost everything runs through you',
      body: "Your answers describe a business where most decisions are waiting for you. Usually that isn't a sign of poor management — it's a business that grew faster than its structure, and the structure never caught up.",
      focus: "Worth checking: the one thing that, if it were solved this month, would free up most of your time — and what it's costing you today, in money and in hours.",
    },
  },
  developing: {
    he: {
      title: 'העסק עובד — אבל עדיין נשען עליך',
      body: 'חלק מהמנגנונים בעסק כבר עומדים בעצמם, וחלק עדיין עוברים דרכך. זה בדיוק השלב שבו צמיחה מתחילה לעלות יותר ממה שהיא מחזירה: כל גידול בפעילות מגיע עם גידול בעומס עליך.',
      focus: 'שווה לבדוק: אילו שתיים או שלוש החלטות חוזרות אליך כל שבוע, ומה חסר לאנשים סביבך כדי לקבל אותן בעצמם.',
    },
    en: {
      title: 'The business works — but it still leans on you',
      body: 'Some parts of the business already stand on their own, and some still run through you. This is exactly the stage where growth starts costing more than it returns: every increase in activity comes with an increase in load on you.',
      focus: 'Worth checking: which two or three decisions come back to you every week, and what the people around you are missing in order to make them themselves.',
    },
  },
  established: {
    he: {
      title: 'העסק עומד בעצמו. השאלה היא לאן לוקחים אותו',
      body: 'רוב מה שסימנת מצביע על עסק שממשיך לעבוד גם כשאת/ה לא בחדר. בעסקים במצב הזה החסם כמעט אף פעם אינו הניהול השוטף, אלא החלטה גדולה אחת שנדחית שוב ושוב — כי אין לה דדליין.',
      focus: 'שווה לבדוק: מה באמת מייצר את הרווח ולא רק את המחזור, ומה יקרה למספרים האלה אם הפעילות תגדל פי שניים.',
    },
    en: {
      title: 'The business stands on its own. The question is where you take it',
      body: "Most of what you marked describes a business that keeps working when you're not in the room. At this stage the constraint is almost never day-to-day management — it's one big decision that keeps getting postponed, because nothing forces a deadline on it.",
      focus: 'Worth checking: what actually generates profit rather than just revenue, and what happens to those numbers if activity doubles.',
    },
  },
};

export const readinessTranslations = {
  he: {
    // Header (always shown)
    headerName: 'אבי פדרמן',
    headerRole: 'ייעוץ עסקי וארגוני',
    // Welcome / Start
    welcomeTitle: 'שלום, אני אבי פדרמן',
    welcomeMessage: 'אני מזמין אותך למלא שאלון קצר לבדוק האם העסק שלך ממצה את הפוטנציאל שלו.',
    startButton: 'יאללה, בואו נתחיל',
    questionOf: 'שאלה {current} מתוך {total}',
    nextButton: 'הבא',
    // Quiz
    landingSubtitle: '8 שאלות. תענה בכנות. לוקח 30 שניות.',
    submitButton: 'בדוק את התוצאות שלי',
    backButton: 'חזור',
    // Closing — one screen for everyone, whatever the score
    closingTitle: '8 שאלות לא יכולות לאבחן עסק.',
    closingBody: 'אבל הן כן יכולות להצביע על המקומות שבהם שווה לעצור ולהסתכל קצת יותר לעומק.',
    // No second person at all, so the line needs no את/ה and carries no
    // accusation. It also says what the call actually offers.
    closingQuestion: 'רוצה לראות את העסק שלך מבחוץ, לרגע?',
    contactCTA: 'תשאיר/י פרטים ואבי יחזור אליך לשיחה קצרה.',
    // Contact form. Three fields, all required: every field here is a reason
    // to close the page, so the form asks only for what Avi needs in order to
    // pick up the phone. Everything else is what the call is for.
    fullName: 'שם מלא',
    email: 'מייל',
    phone: 'טלפון נייד',
    privacyConsent: 'אני מאשר/ת קבלת חומרים שיווקיים ופניות מכירתיות בהתאם למדיניות הפרטיות. ניתן לבטל בכל עת.',
    privacyPolicy: 'למדיניות הפרטיות',
    sendButton: 'שלח',
    // Done
    thankYouTitle: 'תודה רבה!',
    thankYouSubtitle: 'קיבלנו את הפרטים. אבי יחזור אליך בהקדם.',
    aviBio: 'אבי פדרמן, יותר מ-25 שנות ניסיון בייעוץ עסקי וארגוני. עזר למאות בעלי עסקים לקפוץ למדרגה הבאה.',
    visitSiteButton: 'בינתיים, היכנס לאתר פדרמן',
  },
  en: {
    headerName: 'Avi Federman',
    headerRole: 'Business & Organizational Consulting',
    // Welcome / Start
    welcomeTitle: 'Hello, I am Avi Federman',
    welcomeMessage: 'I invite you to fill out a short questionnaire to check if your business is realizing its full potential.',
    startButton: "Yalla, let's start",
    questionOf: 'Question {current} of {total}',
    nextButton: 'Next',
    landingSubtitle: '8 questions. Answer honestly. Takes 30 seconds.',
    submitButton: 'Check my results',
    backButton: 'Back',
    closingTitle: "8 questions can't diagnose a business.",
    closingBody: "But they can point to the places where it's worth stopping and looking a little deeper.",
    closingQuestion: 'Want to see your business from the outside, for a moment?',
    contactCTA: 'Leave your details and Avi will get back to you for a short call.',
    fullName: 'Full name',
    email: 'Email',
    phone: 'Mobile Phone',
    privacyConsent: 'I agree to receive marketing materials and sales communications according to the privacy policy. Unsubscribe anytime.',
    privacyPolicy: 'Privacy Policy',
    sendButton: 'Send',
    thankYouTitle: 'Thank you!',
    thankYouSubtitle: 'We received your details. Avi will get back to you shortly.',
    aviBio: 'Avi Federman, 25+ years of business & organizational consulting. Helped hundreds of business owners reach their next breakthrough.',
    visitSiteButton: 'Meanwhile, visit the Federman website',
  },
};
